import { Box, Divider, useMediaQuery, SvgIcon, IconButton, Badge } from '@mui/material'
import PropTypes from 'prop-types'
import { forwardRef, useImperativeHandle, useRef } from 'react'

import FilterIcon from '@hapana/shared/web/styles/assets/svgs/filter-small.svg'
import Button from '../../../button/button.component'
import { BUTTON_SIZE, BUTTON_THEME, BUTTON_VARIANTS } from '../../../button/button.constants'
import { commonStyles } from '@hapana/shared/web/styles/common.styles'

import TablePagination from '../table-pagination.component'
import { styles as tableStyles, tabletStyles } from '../../table.styles'
import { styles as toolbarStyles, handleStylesWithProps } from './table-toolbar.styles'

const TableToolbar = forwardRef(
  (
    {
      toolbar,
      toolbarWithBorder,
      pagination,
      secondaryAction,
      primaryAction,
      hidePaginationUI,
      otherActions,
      customActions,
      filterProps,
      areAllFiltersDisabled,
    },
    ref
  ) => {
    const primaryActionRef = useRef()
    const secondaryActionRef = useRef()

    useImperativeHandle(ref, () => ({
      get primaryAction() {
        return primaryActionRef.current
      },
      get secondaryAction() {
        return secondaryActionRef.current
      },
    }))

    const isMobileOrTablet = useMediaQuery((theme) => theme.breakpoints.down('md'))

    const {
      otherActions: otherActionData,
      childActionStyles,
      parentActionStyles,
    } = otherActions ?? {}

    return (
      <>
        <Box
          sx={[
            tableStyles.headerContainer,
            handleStylesWithProps({ toolbarWithBorder }),
            { width: Boolean(toolbar) ? { xs: '100%' } : { xs: '100%', md: 'unset' } },
          ]}
        >
          {toolbar && <Box sx={toolbarStyles.boxWrapper}>{toolbar}</Box>}
          <Box sx={toolbarStyles.actionContainer}>
            <Box
              sx={
                isMobileOrTablet
                  ? { ...parentActionStyles }
                  : tableStyles.otherActionParentContainer
              }
            >
              {otherActionData &&
                otherActionData.map((action, idx) => (
                  <Box
                    key={idx}
                    sx={
                      isMobileOrTablet ? { ...childActionStyles } : tableStyles.otherActionContainer
                    }
                  >
                    {action}
                  </Box>
                ))}
            </Box>
            {isMobileOrTablet && customActions && (
              <Box
                sx={[
                  tableStyles.mobileTabletCustomActionsContainer,
                  commonStyles.spaceBetweenFlexRow,
                  commonStyles.fullWidth,
                ]}
              >
                {customActions}
                {filterProps?.onPushFilterDialog && (
                  <Box sx={tabletStyles.customIconButton}>
                    <Badge
                      badgeContent={filterProps?.activeFilterCount}
                      color="primary"
                      invisible={filterProps?.activeFilterCount === 0}
                    >
                      <IconButton
                        onClick={filterProps.onPushFilterDialog}
                        sx={[tabletStyles.iconButton]}
                        disabled={areAllFiltersDisabled}
                      >
                        <SvgIcon component={FilterIcon} sx={tabletStyles.filterIcon} />
                      </IconButton>
                    </Badge>
                  </Box>
                )}
              </Box>
            )}

            {!isMobileOrTablet && customActions}
            {secondaryAction && (
              <Box sx={tableStyles.secondaryActionContainer}>
                <Button
                  test-id={`table-toolbar-secondary-button`}
                  variant={BUTTON_VARIANTS.CONTAINED}
                  startIcon={secondaryAction.icon}
                  onClick={secondaryAction.action}
                  label={secondaryAction.label}
                  theme={BUTTON_THEME.SECONDARY}
                  sx={[tableStyles.secondaryAction, secondaryAction.styles]}
                  type="button"
                  size={secondaryAction.size ?? BUTTON_SIZE.SMALL}
                  ref={secondaryActionRef}
                />
              </Box>
            )}
            {primaryAction && !isMobileOrTablet && (
              <Button
                test-id={`table-toolbar-primary-button`}
                variant={BUTTON_VARIANTS.CONTAINED}
                startIcon={primaryAction.icon}
                onClick={primaryAction.action}
                label={primaryAction.label}
                type="button"
                size={
                  primaryAction.size ?? (isMobileOrTablet ? BUTTON_SIZE.MEDIUM : BUTTON_SIZE.SMALL)
                }
                sx={tableStyles.primaryAction}
                ref={primaryActionRef}
              />
            )}
            {primaryAction && isMobileOrTablet && (
              <Box sx={[commonStyles.verticallyCenteredFlexRow, tabletStyles.buttonWrapper]}>
                <Button
                  test-id={`table-toolbar-primary-button`}
                  variant={BUTTON_VARIANTS.CONTAINED}
                  startIcon={primaryAction.icon}
                  onClick={primaryAction.action}
                  label={primaryAction.label}
                  type="button"
                  size={BUTTON_SIZE.MEDIUM}
                  sx={tableStyles.primaryAction}
                  ref={primaryActionRef}
                />
                {filterProps?.onPushFilterDialog && (
                  <Badge
                    badgeContent={filterProps?.activeFilterCount}
                    color="primary"
                    invisible={filterProps?.activeFilterCount === 0}
                  >
                    <IconButton
                      onClick={filterProps.onPushFilterDialog}
                      sx={[tabletStyles.iconButton]}
                    >
                      <SvgIcon component={FilterIcon} sx={tabletStyles.filterIcon} />
                    </IconButton>
                  </Badge>
                )}
              </Box>
            )}
            {(primaryAction || secondaryAction) && !hidePaginationUI && !isMobileOrTablet && (
              <Divider orientation="vertical" sx={tableStyles.toolbarDivider} />
            )}
            {pagination && !isMobileOrTablet && (
              <TablePagination hidePaginationUI={hidePaginationUI} {...pagination} />
            )}
          </Box>
        </Box>
      </>
    )
  }
)

TableToolbar.propTypes = {
  pagination: PropTypes.shape({
    onPageSizeChange: PropTypes.func.isRequired,
    pageSizeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      })
    ),
    canGoBack: PropTypes.bool.isRequired,
    canGoForward: PropTypes.bool.isRequired,
    onNextPage: PropTypes.func.isRequired,
    onPreviousPage: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    totalResultsQuantity: PropTypes.number,
    currentPageRecords: PropTypes.number.isRequired,
    isLoading: PropTypes.bool.isRequired,
  }),
  primaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    action: PropTypes.func.isRequired,
    size: PropTypes.string,
  }),
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
    action: PropTypes.func.isRequired,
    props: PropTypes.object,
    styles: PropTypes.object,
    variant: PropTypes.string,
    badge: PropTypes.bool,
  }),
  hidePaginationUI: PropTypes.bool,
  otherActions: PropTypes.array,
  customActions: PropTypes.node,
  filterProps: PropTypes.object,
  areAllFiltersDisabled: PropTypes.bool,
}

TableToolbar.defaultProps = {
  hidePaginationUI: false,
}

export default TableToolbar
